import { useEffect, useRef, useState } from 'react';

// Counts up from 0 to `to` (with optional suffix) the first time the element scrolls
// into view. Returns { ref, value } — attach ref to the display node and render value.
export function useCountUp(to, suffix = '', duration = 1400) {
  const ref = useRef(null);
  const [value, setValue] = useState('0' + suffix);
  const startedRef = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const target = parseFloat(to);
    if (!isFinite(target)) {
      setValue(String(to));
      return undefined;
    }

    const ease = (t) => 1 - Math.pow(1 - t, 3);

    const run = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const start = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - start) / duration);
        setValue(Math.round(target * ease(t)) + suffix);
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            run();
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(node);

    return () => io.disconnect();
  }, [to, suffix, duration]);

  return { ref, value };
}
